import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star, MapPin, Package, Zap, Video } from 'lucide-react';
import { Button } from '../shared';
import { getVendorConsultationSettings } from '../../services/consultationService';
import BookConsultationModal from './BookConsultationModal';
import '../../styles/bookingWidget.css';

const BookingWidget = ({
  service,
  vendor,
  category,
  startingPrice,
  hasPackages,
  serviceRating,
  serviceReviews,
  isAuthenticated,
  navigate,
  formatPrice,
}) => {
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const vendorId = vendor?.id;
  const { data: consultationData } = useQuery({
    queryKey: ['vendorConsultationSettings', vendorId],
    queryFn: () => getVendorConsultationSettings(vendorId),
    enabled: !!vendorId,
  });
  const consultationEnabled = consultationData?.data?.consultation_enabled ?? false;
  const consultationQuestions = consultationData?.data?.consultation_questions ?? [];
  const packageCount = service?.vendor_service_packages?.length || 0;

  return (
    <div className="border border-gray-200 rounded-lg p-6 shadow-lg bg-white animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="mb-6">
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-2xl font-light text-black price-animate">
            ${formatPrice(startingPrice)}
          </span>
          <span className="text-base font-light text-gray-600 animate-in fade-in duration-500 delay-200">
            starting price
          </span>
        </div>
        {hasPackages ? (
          <>
            <Button
              variant="primary"
              size="md"
              className="w-full !rounded-lg !py-3 text-base font-light !bg-black hover:!bg-gray-800 !text-white animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300"
              icon={Zap}
              onClick={() => {
                if (isAuthenticated) {
                  navigate(`/booking/${service.id}`);
                } else {
                  navigate("/login", {
                    state: {
                      from: { pathname: `/booking/${service.id}` },
                    },
                  });
                }
              }}
            >
              Book This Service
            </Button>
            <p className="text-center text-xs text-gray-500 mt-3 font-light animate-in fade-in duration-500 delay-500">
              You won't be charged yet
            </p>
            {consultationEnabled && (
              <Button
                variant="outline"
                size="md"
                className="w-full !rounded-lg !py-3 text-base font-light mt-3 border-2 border-gray-900 text-gray-900 animate-in fade-in duration-500 delay-400"
                icon={Video}
                onClick={() => {
                  if (isAuthenticated) {
                    setShowConsultationModal(true);
                  } else {
                    navigate('/login', { state: { from: { pathname: `/services/${service.id}` } } });
                  }
                }}
              >
                Book Consultation
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="primary"
              size="md"
              className="w-full !rounded-lg !py-3 text-base font-light !bg-gray-400 !cursor-not-allowed !text-white animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300"
              icon={Zap}
              disabled
            >
              Book This Service
            </Button>
            <p className="text-center text-xs text-gray-500 mt-3 font-light animate-in fade-in duration-500 delay-500">
              Packages not added yet. Please check back later to book.
            </p>
          </>
        )}
      </div>

      <div className="pt-6 border-t border-gray-200">
        <div className="space-y-4 text-sm">
          <div 
            className="flex items-center justify-between detail-item"
            style={{ animationDelay: '600ms' }}
          >
            <span className="text-gray-600 font-light">Starting Price</span>
            <span className="font-light text-black">
              ${formatPrice(startingPrice)}
            </span>
          </div>
          
          {category && (
            <div 
              className="flex items-center justify-between detail-item"
              style={{ animationDelay: '700ms' }}
            >
              <span className="text-gray-600 font-light">Category</span>
              <span className="font-light text-black capitalize">
                {category.category_name}
              </span>
            </div>
          )}

          {packageCount > 0 && (
            <div 
              className="flex items-center justify-between detail-item"
              style={{ animationDelay: '800ms' }}
            >
              <span className="text-gray-600 font-light">Packages Available</span>
              <div className="flex items-center gap-1">
                <Package className="w-4 h-4 text-gray-600 icon-animate" style={{ animationDelay: '900ms' }} />
                <span className="font-light text-black">{packageCount}</span>
              </div>
            </div>
          )}

          {serviceRating > 0 && (
            <div 
              className="flex items-center justify-between detail-item"
              style={{ animationDelay: '900ms' }}
            >
              <span className="text-gray-600 font-light">Rating</span>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 star-blink" />
                <span className="font-light text-black">
                  {typeof serviceRating === 'number' && !isNaN(serviceRating) ? serviceRating.toFixed(1) : '0.0'}
                  {serviceReviews > 0 && (
                    <span className="text-gray-500 ml-1">
                      ({serviceReviews})
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}

          {vendor?.city && vendor?.state && (
            <div 
              className="flex items-center justify-between detail-item"
              style={{ animationDelay: '1000ms' }}
            >
              <span className="text-gray-600 font-light">Location</span>
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4 text-gray-600 icon-animate" style={{ animationDelay: '1100ms' }} />
                <span className="font-light text-black">
                  {vendor.city}, {vendor.state}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* <div className="pt-6 border-t border-gray-200 animate-in fade-in duration-500 delay-1100">
        <button className="text-sm font-light text-black underline w-full text-center hover:text-gray-600 transition-colors">
          Report this listing
        </button>
      </div> */}

      <BookConsultationModal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        vendorId={vendorId}
        vendorName={vendor?.business_name}
        serviceId={service?.id}
        questions={consultationQuestions}
      />
    </div>
  );
};

export default BookingWidget;

